import React, { useState, useRef, useEffect } from "react";
import { useLanguage, Language, languageNames, languageCodes } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { FiSettings, FiX, FiGlobe, FiMoon, FiSun } from "react-icons/fi";

// Tab types for settings panel
type SettingsTab = 'language' | 'appearance';

const FloatingLanguageSelector: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const { isDarkMode, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingsTab>('language');
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Render icons as functions
  const renderIcon = (Icon: any, size: number) => {
    return Icon({ size });
  };

  return (
    <div className="floating-language-selector" ref={menuRef}>
      <button 
        className={`floating-lang-button ${isOpen ? 'active' : ''}`}
        onClick={toggleMenu}
        aria-label={t('settings.aria_label')}
      >
        {isOpen ? renderIcon(FiX, 20) : renderIcon(FiSettings, 20)}
      </button>
      
      {isOpen && (
        <div className="floating-settings-panel">
          <div className="settings-tabs">
            <button 
              className={`tab-button ${activeTab === 'language' ? 'active' : ''}`}
              onClick={() => setActiveTab('language')}
            >
              {renderIcon(FiGlobe, 16)}
              <span>{t('settings.language')}</span>
            </button>
            <button 
              className={`tab-button ${activeTab === 'appearance' ? 'active' : ''}`}
              onClick={() => setActiveTab('appearance')}
            >
              {isDarkMode ? renderIcon(FiMoon, 16) : renderIcon(FiSun, 16)}
              <span>{t('settings.appearance')}</span>
            </button>
          </div>
          
          <div className="settings-content">
            {activeTab === 'language' && (
              <div className="language-options">
                {(Object.keys(languageNames) as Language[]).map((lang) => (
                  <button 
                    key={lang}
                    className={`lang-option ${language === lang ? 'active' : ''}`}
                    onClick={() => handleLanguageChange(lang)}
                  >
                    <span className="lang-flag">{languageCodes[lang]}</span>
                    <span className="lang-name">{languageNames[lang]}</span>
                  </button>
                ))}
              </div>
            )}
            
            {activeTab === 'appearance' && (
              <div className="appearance-options">
                <div className="theme-toggle">
                  <span className="theme-label">{t('settings.dark_mode')}</span>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={isDarkMode}
                      onChange={toggleTheme}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
                <div className="theme-preview">
                  <div className={`preview-box light-preview ${!isDarkMode ? 'active' : ''}`} onClick={() => !isDarkMode || toggleTheme()}>
                    {renderIcon(FiSun, 16)}
                    <span>{t('settings.light')}</span>
                  </div>
                  <div className={`preview-box dark-preview ${isDarkMode ? 'active' : ''}`} onClick={() => isDarkMode || toggleTheme()}>
                    {renderIcon(FiMoon, 16)}
                    <span>{t('settings.dark')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingLanguageSelector;